"use client";

import React, { useMemo, useState } from "react";
import { Plus, Minus } from "lucide-react";
import shopKaBotImage from "../../assets/images/ShopKaBot.jpeg";

type Item = {
  title: string;
  body: string;
};

export default function SolutionsLikeSection() {
  const items: Item[] = useMemo(
    () => [
      {
        title: "Are you struggling to respond quickly?",
        body: "ShopKaBot provides instant responses 24/7 on WhatsApp, ensuring you never miss a sales opportunity due to delayed replies.",
      },
      {
        title: "Have to stay awake to handle your customer queries?",
        body: "ShopKaBot works 24/7 on WhatsApp, answering customer questions even when you’re offline—capturing sales around the clock.",
      },
      {
        title: "Slow replies decreasing your sales?",
        body: "ShopKaBot provides instant responses 24/7 on WhatsApp, ensuring you never miss a sales opportunity due to delayed replies.",
      },
      {
        title: "Losing customers after the first chat?",
        body: "ShopKaBot sends automated follow-ups on WhatsApp, reminding customers about abandoned carts, new arrivals, and offers—bringing them back to complete their purchase.",
      },
      {
        title: "Not able to handle multiple customer chats?",
        body: "ShopKaBot manages multiple WhatsApp conversations simultaneously, ensuring no customer is left waiting and maximizing your sales opportunities.",
      },
    ],
    [],
  );

  const [openIndex, setOpenIndex] = useState<number>(0);

  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-6xl px-4">
        {/* Heading at the top */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900">
            Problems we solve to increase your sales through WhatsApp
          </h2>
          <p className="mt-3 mx-auto max-w-md text-sm leading-relaxed text-slate-600">
            Each solution removes a sales blocker on WhatsApp—so more chats
            become orders, and more customers come back.
          </p>
        </div>

        <div className="grid items-start gap-10 lg:grid-cols-12">
          {/* LEFT - Image */}
          <div className="lg:col-span-7">
            <img
              src={shopKaBotImage}
              alt="ShopKaBot"
              className="h-auto w-full object-cover"
            />
          </div>

          {/* RIGHT - Accordion */}
          <div className="lg:col-span-5">
            <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
              {items.map((it, idx) => {
                const isOpen = idx === openIndex;
                return (
                  <button
                    key={it.title}
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                    className="w-full py-5 text-left"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div
                        className={`text-sm font-semibold ${
                          isOpen ? "text-slate-900" : "text-slate-500"
                        }`}
                      >
                        {it.title}
                      </div>

                      <span className="flex h-8 w-8 items-center justify-center text-slate-500">
                        {isOpen ? (
                          <Minus className="h-5 w-5" />
                        ) : (
                          <Plus className="h-5 w-5" />
                        )}
                      </span>
                    </div>

                    {isOpen && (
                      <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600">
                        {it.body}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Optional one-liner summary */}
            <p className="mt-4 text-xs text-slate-500">
              Result: faster responses + better product guidance + consistent
              follow-ups = more sales.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
